/*
 * Copyright (c) Dito Technologies LLC
 */

import React, {useState} from "react";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {PieceRenderer_piece$data} from "ui/feature/piece-play/common/__generated__/PieceRenderer_piece.graphql";
import styles from "ui/feature/piece-play/common/piece-renderer/PieceRenderer.module.scss";
import PieceRendererStaff from "ui/feature/piece-play/common/piece-renderer/PieceRendererStaff";
import PieceRendererNote, {widthForWholeNote} from "ui/feature/piece-play/common/piece-renderer/PieceRendererNote";
import PieceRendererCurrentPlayedNote from "ui/feature/piece-play/common/piece-renderer/PieceRendererCurrentPlayedNote";
import {useDrop} from "react-dnd";
import DndItemTypes, {NoteItem} from "ui/feature/piece-play/common/DndItemTypes";
import PieceRendererNoteDisplay from "ui/feature/piece-play/common/piece-renderer/PieceRendererNoteDisplay";
import MIDIToLineFromTrebleClef from "util/MIDIToLineFromTrebleClef";
import PitchToName, {IsSharpOrFloat, LineIDXToPitch, MIDITOPitch} from "util/pitch";
import UpdatePieceFileChangeMIDINoteAndStart from "mutation/UpdatePieceFileChangeMIDINoteAndStart";
import PlayType from "ui/feature/piece-play/common/PlayType";
import {BulbIdToStateLookup} from "ui/feature/piece-play/common/PlayTypes";

interface Props {
    piece: PieceRenderer_piece$data;
    playPosition: number;
    currentMidiNotes: number[];
    bulbIdToStateLookup: BulbIdToStateLookup;
    playType: PlayType;
}

const PIECE_START_PADDING = 0.75 * widthForWholeNote;
const halfLineHeight = 27 / 2;

const leftHandledFilter = (note: { left_handed: boolean }) => {
    return note.left_handed;
}

const rightHandedFilter = (note: { left_handed: boolean }) => {
    return !note.left_handed
}

const passthroughFilter = (_: { left_handed: boolean }) => {
    return true
}

const PieceRenderer: React.FC<Props> = ({piece, playType, playPosition, bulbIdToStateLookup, currentMidiNotes}) => {

    const [draggingItem, setDraggingItem] = useState<{ bulbId: string, midi: number, lineDy: number, noteDuration: number, noteStart: number, name: string } | null>(null);

    const [, drop] = useDrop(() => ({
        accept: DndItemTypes.NOTE,
        drop: async (item: NoteItem, monitor) => {
            const dy = Math.round((monitor.getDifferenceFromInitialOffset()?.y ?? 0) / halfLineHeight)
            const dx = Math.round((monitor.getDifferenceFromInitialOffset()?.x ?? 0) / (widthForWholeNote / 32)) / 32;
            let currMidi = item.midiNumber;
            let currLineIdx = MIDIToLineFromTrebleClef(item.midiNumber) || 0;
            const targetLineIdx = currLineIdx + dy;
            while (currLineIdx !== targetLineIdx || IsSharpOrFloat(MIDITOPitch(currMidi))) {
                if (currLineIdx > targetLineIdx) {
                    currMidi += 1;
                } else {
                    currMidi -= 1;
                }
                const nextLineIdx = MIDIToLineFromTrebleClef(currMidi);
                if (nextLineIdx === undefined) {
                    throw new Error("could not get line idx from midi, this is an error")
                }
                currLineIdx = nextLineIdx;
            }
            await UpdatePieceFileChangeMIDINoteAndStart(piece.id, item.bulbId, currMidi, item.noteStart + dx)
            setDraggingItem(null);
        },
        hover: (item: NoteItem, monitor) => {
            const dy = Math.round((monitor.getDifferenceFromInitialOffset()?.y ?? 0) / halfLineHeight)
            const dx = Math.round((monitor.getDifferenceFromInitialOffset()?.x ?? 0) / (widthForWholeNote / 32)) / 32;
            const lineIdx = (MIDIToLineFromTrebleClef(item.midiNumber) || 0) + dy;
            const pitch = LineIDXToPitch(lineIdx)
            const name = PitchToName(pitch);
            setDraggingItem({
                bulbId: item.bulbId,
                midi: item.midiNumber,
                noteDuration: item.noteDuration,
                noteStart: item.noteStart + dx,
                lineDy: dy,
                name: name
            })
        },
    }))

    const startPadding = React.useMemo(() => {
        return (-playPosition * widthForWholeNote) + PIECE_START_PADDING; // initial start
    }, [playPosition]);

    const filter = React.useMemo(() => {
        if (playType === "both_hands") {
            return passthroughFilter
        } else if (playType === "right_hand") {
            return rightHandedFilter;
        } else {
            return leftHandledFilter;
        }
    }, [playType])

    if (!piece.file) {
        return <p>No piece file. This is an error. Please reach out to support.</p>;
    }

    return (
        <div className={styles.container} ref={drop}>
            <div className={styles.playLine} style={{
                left: `${PIECE_START_PADDING}px`
            }}/>
            <div className={styles.noteWrapper} style={{
                left: `${startPadding}px`
            }}>
                {draggingItem && <PieceRendererNoteDisplay
                    notePitchTitle={draggingItem.name}
                    lineAdjustment={draggingItem.lineDy}
                    midiNumber={draggingItem.midi}
                    noteDuration={draggingItem.noteDuration}
                    noteStart={draggingItem.noteStart}
                    noteState={"none"}/>}
                {currentMidiNotes.map((midi) => {
                    return <PieceRendererCurrentPlayedNote key={midi}
                                                           middleLinePadding={startPadding - PIECE_START_PADDING}
                                                           midi={midi}/>;
                })}
                {piece.file.notes.filter(filter).map((note) => (
                    <PieceRendererNote key={note.bulb_id} note={note} pieceId={piece.id}
                                       noteState={bulbIdToStateLookup[note.bulb_id] || "none"}/>
                ))}
            </div>
            <PieceRendererStaff/>
            <div className={styles.staffSpacer}/>
            <PieceRendererStaff/>
        </div>
    );
}

export default createFragmentContainer(PieceRenderer, {
    piece: graphql`
    fragment PieceRenderer_piece on MusicPiece {
        id
        output_storage_name
        file {
            notes {
                bulb_id
                left_handed
                note_start
                ...PieceRendererNote_note
            }
        }
    }
    `
});